import { useIsRecordingTimerRunning } from '@/hooks/useIsSessionWithoutRecording';
import {
  hasInFlightChunkUploadsForSession,
  hasRetainedChunksForSession,
  listSessionPersistedAudioStatuses,
  subscribeInFlightUploads,
  useSessionRecordingStore,
} from '@heidi/capture';
import { logger } from '@heidi/runtime/logger';
import {
  type ClientTranscriptDeviceSignals,
  useQuerySessionById,
} from '@heidi/session/data';
import { useLiveQuery } from 'dexie-react-hooks';
import { useSyncExternalStore } from 'react';

type LedgerSignals = Pick<
  ClientTranscriptDeviceSignals,
  'persistedChunkStatuses' | 'persistedBackupStatuses'
>;

const UNSETTLED_LEDGER: LedgerSignals = {
  persistedChunkStatuses: 'unknown',
  persistedBackupStatuses: 'unknown',
};

/** Echoes `readFor` so a result can be matched to the deps that produced it. */
const readLedger = async (readFor: {
  sessionId: string;
  recordingTimerRunning: boolean;
}) => {
  try {
    const { chunks, backups } = await listSessionPersistedAudioStatuses(
      readFor.sessionId
    );
    const ledger: LedgerSignals = {
      persistedChunkStatuses: chunks,
      persistedBackupStatuses: backups,
    };
    return { readFor, ledger };
  } catch (error) {
    logger.warn('Persisted audio status for the transcript claim failed', {
      errorName: error instanceof Error ? error.name : typeof error,
    });
    const ledger: LedgerSignals = {
      persistedChunkStatuses: 'unreadable',
      persistedBackupStatuses: 'unreadable',
    };
    return { readFor, ledger };
  }
};

const getServerInFlightUploads = () => false;

/**
 * The session package's device-signal port for the "transcript is safe" claim.
 * Judged for `sessionId`, never the capture store's route `sessionId`; a
 * running timer with no owner counts for every session.
 */
export function useClientTranscriptDeviceSignals(
  sessionId: string
): ClientTranscriptDeviceSignals {
  const recordingTimerRunning = useIsRecordingTimerRunning(sessionId);
  const liveCaptureInProgress = useSessionRecordingStore(
    (state) =>
      state.activeRecordingKey?.sessionId === sessionId &&
      (state.captureSlot !== null ||
        Object.values(state.chunkRecorders).some((recorder) => !recorder.doneRecording))
  );
  const inFlightChunkUploads = useSyncExternalStore(
    subscribeInFlightUploads,
    () => hasInFlightChunkUploadsForSession(sessionId),
    getServerInFlightUploads
  );
  // Retained chunks change inside an upload slot (see `retainFailedChunk`).
  const retainedChunks = useSyncExternalStore(
    subscribeInFlightUploads,
    () => hasRetainedChunksForSession(sessionId),
    getServerInFlightUploads
  );
  const { data: chunkUploadProgress } = useQuerySessionById({
    sessionId,
    select: (session) => session?.chunkUploadProgress,
  });
  // Re-read when this session's recording stops: a ledger created by that
  // recording is not observed by a query that found no ledger at all.
  const read = useLiveQuery(
    () => readLedger({ sessionId, recordingTimerRunning }),
    [sessionId, recordingTimerRunning]
  );
  // `useLiveQuery` returns the previous deps' result until the new read settles.
  const ledger =
    read?.readFor.sessionId === sessionId &&
    read.readFor.recordingTimerRunning === recordingTimerRunning
      ? read.ledger
      : UNSETTLED_LEDGER;

  return {
    liveChunkUploadInProgress:
      (chunkUploadProgress !== undefined && chunkUploadProgress < 100) ||
      liveCaptureInProgress,
    inFlightChunkUploads,
    retainedChunks,
    ...ledger,
    recordingTimerRunning,
  };
}
